import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  Coupon,
  CouponCreateInput,
  CouponListParams,
  CouponRedemption,
  CouponRedemptionListParams,
  CouponUpdateInput,
  PointsTier,
  PointsTierInput,
} from '../types/coupons.types';

/**
 * Coupons Admin API. The backend re-checks every country (and every coupon's and customer's
 * country) against the staff member's scope, and every permission.
 */

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

function toPaged<T>(page: AdminPage<T>): PagedResponse<T> {
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

export async function getCoupons(
  countryId: number,
  params: CouponListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<Coupon>> {
  return toPaged(
    await http.get<AdminPage<Coupon>>(`/admin/countries/${countryId}/coupons`, { params, signal }),
  );
}

export async function getCoupon(id: number, signal?: AbortSignal) {
  return http.get<Coupon>(`/admin/coupons/${id}`, { signal });
}

/** coupons.manage. 409 COUPON_CODE_EXISTS on the code field. */
export async function createCoupon(countryId: number, input: CouponCreateInput) {
  return http.post<Coupon>(`/admin/countries/${countryId}/coupons`, input);
}

/** coupons.manage. 409 COUPON_LOCKED when a used coupon's value or brands would change. */
export async function updateCoupon(id: number, input: CouponUpdateInput) {
  return http.put<{ changed: number }>(`/admin/coupons/${id}`, input);
}

export async function setCouponActive(id: number, active: boolean) {
  return http.post<{ changed: number }>(
    `/admin/coupons/${id}/${active ? 'activate' : 'deactivate'}`,
  );
}

export async function getCouponRedemptions(
  id: number,
  params: CouponRedemptionListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<CouponRedemption>> {
  return toPaged(
    await http.get<AdminPage<CouponRedemption>>(`/admin/coupons/${id}/redemptions`, {
      params,
      signal,
    }),
  );
}

export async function getCustomerCoupons(customerId: number, signal?: AbortSignal) {
  return http.get<Coupon[]>(`/admin/customers/${customerId}/coupons`, { signal });
}

export async function getPointsTiers(countryId: number, signal?: AbortSignal) {
  return http.get<PointsTier[]>(`/admin/countries/${countryId}/points-tiers`, { signal });
}

export async function createPointsTier(countryId: number, input: PointsTierInput) {
  return http.post<PointsTier>(`/admin/countries/${countryId}/points-tiers`, input);
}

export async function updatePointsTier(id: number, input: PointsTierInput) {
  return http.put<{ changed: number }>(`/admin/points-tiers/${id}`, input);
}

export async function setPointsTierActive(id: number, active: boolean) {
  return http.post<{ changed: number }>(
    `/admin/points-tiers/${id}/${active ? 'activate' : 'deactivate'}`,
  );
}
